<template>
    <div ref="validator" class="validator">
        <div id="trace"></div>
        <div class="validator__errors" v-if="props.showFormErrorSummary"></div>
        <slot ref="form" name="form"></slot>
    </div>
</template>

<script setup>
    import { ref, onMounted, onUpdated, useSlots } from 'vue';
    import leven from 'leven';
    import * as yup from 'yup';

    import domains from './data/domains.json' with { type: 'json' };
    import extensions from './data/extensions.json' with { type: 'json' };

    let allDomains;

    const validator = ref("validator");
    const slots = useSlots();

    const props = defineProps({
        blockedDomains: String,
        blockedEmailWarning: String,
        commonDomains: String,
        errorPosition: String,
        showFormErrorSummary: Boolean
    });

    const domainRegex = /(?!@)[a-z]+\.[a-z0-9]+(\.([a-z]+))*$/gi;
    const extRegex          = /(\.[^.@]{2,}){1,2}$/;

    // We'll add the fields from the DOM later
    let fields;

    // Keep track of the fields and their validation schema
    let fieldSchemas;

    // Track field errors
    let fieldErrors;

    const setupForm = () => {
        formatPrefixedInputs();
        setupFields();
        setupFieldsSchema();
        setupChangeEvents();
    };

    const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const parseErrorMessages = (field) => {
        if (field.dataset.errorMessages == null) return null;
        try {
            return JSON.parse(field.dataset.errorMessages);
        } catch {
            return null;
        }
    };

    const parseSuggestionMessages = (field) => {
        if (field.dataset.suggestionMessages == null) return null;
        try {
            return JSON.parse(field.dataset.suggestionMessages);
        } catch {
            return null;
        }
    }

    // Build a DOM element for field errors — uses textContent to prevent XSS
    const buildErrorsMarkup = (field, response, name, anchor = false) => {
        const form = validator._value.querySelector("form");
        const errors = response.errors;

        if (typeof field == "string") {
            field = form.querySelector(`[id*="${field.replace("[]", "")}"]`);
        }

        const ul = document.createElement('ul');
        ul.className = 'ff-errors';
        ul.id = `${name}-errors`;
        ul.setAttribute('role', 'alert');

        errors.forEach(item => {
            const li = document.createElement('li');
            if (anchor && field && field.id) {
                const a = document.createElement('a');
                a.href = `#${field.id}`;
                a.textContent = item;
                li.appendChild(a);
            } else {
                li.textContent = item;
            }
            ul.appendChild(li);
        });

        return ul;
    };

    const buildSchema = (field, isGroup = false) => {
        if (isGroup) {
            if (field.length > 1) {
                const errorMessages = parseErrorMessages(field[0]);
                const required      = field[0].classList.contains("freeform-required");
                const requiredMsg   = errorMessages?.required ?? undefined;

                let schema = yup.array();
                if (required) {
                    const minCount = errorMessages?.min != null ? Number(errorMessages.min) : 1;
                    schema = schema.min(minCount, requiredMsg)
                        .of(yup.string().required())
                        .required(requiredMsg);
                } else {
                    schema = schema.of(yup.string()).nullable();
                }
                return schema;
            } else {
                field = field[0];
                const errorMessages = parseErrorMessages(field);
                let schema = yup.string();

                if (field.classList.contains("freeform-required")) {
                    schema = schema.notOneOf([null], 'This field is required');
                } else {
                    schema = schema.nullable();
                }
                return schema;
            }
        } else {
            const errorMessages = parseErrorMessages(field);
            let schema;

            const requiredMsg = errorMessages?.required ?? 'The field is required.';

            switch (field.type) {
                case "checkbox":
                    schema = yup.string();
                    if (field.classList.contains("freeform-required")) {
                        schema = schema.notOneOf([null], 'This field is required');
                    } else {
                        schema = schema.nullable();
                    }
                    break;
                case "email": {
                    schema = yup.string();
                    schema = field.classList.contains("freeform-required") ? schema.required(requiredMsg) : schema;
                    schema = schema.email(errorMessages?.email ?? undefined);

                    // Add Levenshtein distance comparison for email extension accuracy
                    schema = schema.test({ name: 'distance', skipAbsent: !field.classList.contains("freeform-required"), message: 'Please check email extension.',  test: (value) => {
                        const userExt = value.match(extRegex)[0];
                        let result;

                        for (let i = 0; i < extensions.length; i++) {
                            const levenResult = leven(userExt, extensions[i]);
                            if (typeof result === 'undefined' || levenResult < result) {
                                result = levenResult;
                            }
                            if (result === 0) break; // exact match, no need to continue
                        }

                        return result === 0;
                    } });

                    if (props.blockedDomains) {
                        const warningMsg = props.blockedEmailWarning ?? "Please provide a valid email address.";
                        const escapedDomains = props.blockedDomains.split('|').map(d => escapeRegex(d.trim())).join('|');
                        const blockedRegex = new RegExp(`@(${escapedDomains})`);
                        schema = schema.test('blockedDomain', warningMsg, (value) => !value || !blockedRegex.test(value));
                    }
                    break;
                }
                case "number":
                    schema = yup.number();
                    if (field.min) {
                        schema = schema.min(Number(field.min), errorMessages?.min ?? undefined);
                    }
                    if (field.max) {
                        schema = schema.max(Number(field.max), errorMessages?.max ?? undefined);
                    }
                    break;
                case "tel":
                    schema = yup.string().matches(/^[\+]*[0-9]+$/, { excludeEmptyString: true, message: "Please enter a valid phone number." });
                    break;
                default:
                    schema = yup.string();
                    break;
            }

            if (field.classList.contains("freeform-required")) {
                switch (field.type) {
                    case "number":
                        schema = schema.required(requiredMsg)
                            .transform((cVal, oVal) => oVal === '' ? null : cVal)
                            .typeError('Must be a number');
                        break;
                    default:
                        schema = schema.required(requiredMsg);
                        break;
                }
            } else {
                switch (field.type) {
                    case "number":
                        schema = schema.transform((cVal, oVal) => oVal === '' ? null : cVal)
                            .nullable(true)
                            .typeError('Must be a number');
                        break;
                    default:
                        schema = schema.nullable();
                        break;
                }
            }

            return schema ?? null;
        }
    };

    // Build a DOM element for field suggestions — uses textContent to prevent XSS
    const buildSuggestionMarkup = (field, name, result) => {
        const form = validator._value.querySelector("form");

        if (typeof field == "string") {
            field = form.querySelector(`[id*="${field.replace("[]", "")}"]`);
        }

        const suggestionMessages = parseSuggestionMessages(field);

        const ul = document.createElement('ul');
        ul.className = 'ff-suggestions';
        ul.id = `${name}-suggestions`;
        ul.setAttribute('role', 'alert');

        if (result <= 3) {
            const updateDomain = () => {
                let value = field.value;                
                const newValue = value.replace(domainRegex, suggestion);
                field.value = newValue;

                const change = document.createEvent('HTMLEvents');
                change.initEvent('change', false, true);
                field.dispatchEvent(change);

                return true;
            };

            const suggestion    = getSuggestionMessage(field);
            const li            = document.createElement('li');
            const button        = document.createElement('button');
            button.addEventListener('click', updateDomain); 
            button.innerText    = `Did you mean ${suggestion}?`
            li.appendChild(button);
            ul.appendChild(li);
        } else {
            Object.entries(suggestionMessages).forEach(suggestion => {
            const li = document.createElement('li');

            switch (field.type) {
                case 'email': 
                    li.innerText = suggestion[1];
                    break;
            }
                ul.appendChild(li);
            });
        }

        return ul;
    };

    const findCommonParent = (groupedFields) => {
        let commonParent  = null;
        let parentFound   = false;

        for (const [key, element] of Object.entries(groupedFields)) {
            const parent = element.closest(`[data-field-type*="${element.type}"]`);

            if (groupedFields[key - 1] != null) {
                const prevElementParent = groupedFields[key - 1].closest(`[data-field-type*="${element.type}"]`);

                if (prevElementParent == parent) {
                    commonParent = commonParent == null ? prevElementParent : commonParent;
                    parentFound = true;
                } else {
                    commonParent = parent;
                    parentFound = false;
                }
            } else {
                parentFound = false;
            }
        }

        return commonParent;
    }

    const formatPrefixedInputs = () => {
        const form = validator._value.querySelector("form");
        const prefixedInputs = form.querySelectorAll("input[data-prefix]");

        function formatNumber(event, input) {
            let returnValue = null;
            const parts 		= event.target.value.split(".");
            const value 		= parts[0].replace(/\D/g, "");
            const decimal 	= parts[1] != null ? parts[1].slice(0, 2) : null;

            if (event.target.value == '') {
                setDefault(event, input);
            }

            let newValue;
            const prefix = input.dataset.prefix;

            newValue = new Intl.NumberFormat(input.dataset.numberFormat).format(value);

            if (!isNaN(decimal) && decimal != null) {
                newValue = `${newValue}.${decimal}`;
            }

            returnValue = value === '' && newValue === '0' ? '' : prefix + newValue;

            return returnValue;
        }

        function setDefault(event, input) {
            event.target.value = input.dataset.prefix;

            if (event.target.value.length < 2) {
                input.selectStart = input.selectionEnd = event.target.value.length;
            }
        }

        if (prefixedInputs) {
            prefixedInputs.forEach(input => {
                input.addEventListener("change", event => {
                    if (event.target.value == '') {
                        setDefault(event, input);
                    }
                });

                input.addEventListener("focusin", event => {
                    if (event.target.value == '') {
                        setDefault(event, input);
                    }
                });

                input.addEventListener("focusout", event => {
                    if (event.target.value.length == 1) {
                        event.target.value = null;
                    }
                });

                input.addEventListener("input", event => {
                    event.target.value = formatNumber(event, input);
                });
            });
        }
    };

    const getDomains = () => {
        let allDomains = domains;

        if (typeof props.commonDomains !== 'undefined' && props.commonDomains.length) {

            Object.entries(JSON.parse(props.commonDomains)).forEach(domain => {
                allDomains.push(domain[1]);
            });

            allDomains = [...new Set(allDomains)]; // remove duplicates
            allDomains.sort();

            return allDomains;
        } else {
            return allDomains;
        }
    };

    const getSuggestionMessage = (field) => {
        let suggestionMessage;

        switch (field.type) {
            case 'email':
                const getNearestDomain = (value) => {
                    const userDomain    = value.match(domainRegex)?.[0] ?? '';
                    let result;
                    let suggestion;

                    for (let i = 0; i < allDomains.length; i++) {
                        const levenResult = leven(userDomain, allDomains[i]);
                        if (typeof result === 'undefined' || levenResult < result) {
                            result = levenResult;
                            suggestion = allDomains[i];
                        }
                        if (result === 0) break; // exact match, no need to continue
                    }

                    return suggestion == userDomain ? '' : suggestion;
                };

                suggestionMessage = getNearestDomain(field.value);
                break;
        }

        return suggestionMessage;
    };

    // Return fields if they have the same property
    const groupFields = (fields, property) => {
        let groupedFields = {};

        fields.forEach(field => {
            if (field[property] != null) {
                if (groupedFields[field[property]] == null) {
                    groupedFields[field[property]] = fields.filter(item => item[property] == field[property]);
                }
            }
        });

        return groupedFields;
    };

    const handleAttributes = (field, response) => {
        const form = validator._value.querySelector("form");

        const attributeFunction = (element, response) => {
            if (typeof response == "object" && response.errors != null) {
                element.classList.add("invalid");
                element.classList.remove("valid");
                element.setAttribute("aria-invalid", "true");
                element.setAttribute("aria-describedby", `${field.id}-errors`);
            } else {
                element.classList.add("valid");
                element.classList.remove("invalid");
                element.removeAttribute("aria-invalid");
                element.removeAttribute("aria-describedby");
            }
        };

        if (typeof field == "string") {
            form.querySelectorAll(`[name="${field}"`).forEach(el => {
                attributeFunction(el, response);
            });
        } else {
            attributeFunction(field, response);
        }
    };

    const handleErrorsMarkup = (field, response) => {
        const form = validator._value.querySelector("form");

        function handleMarkup(element, response, position, isParent = false) {
            const parent = isParent ? element : element.parentElement;

            if (typeof response == "object" && response.errors != null) {
                const markupEl = buildErrorsMarkup(field, response, field.name);

                if (parent.querySelector(".ff-errors") != null) {
                    parent.querySelector(".ff-errors").remove();
                }
                element.insertAdjacentElement(position, markupEl);
            } else {
                if (parent != null && parent.querySelector(".ff-errors") != null) {
                    parent.querySelector(".ff-errors").remove();
                }
            }
        }

        if (typeof field == "string" || ["radio", "checkbox"].indexOf(field.type) != -1) {
            const groupedFields = form.querySelectorAll(`[name="${typeof field == "string" ? field : field.name}"]:not([aria-hidden="true"], [type="hidden"]), [name="${typeof field == "string" ? field : field.name}[]"]:not([aria-hidden="true"], [type="hidden"])`);

            let errorPosition = "beforeend";

            if (props.errorPosition) {
                switch (props.errorPosition) {
                    case "beforeInput":
                        errorPosition = "afterbegin";
                        break;
                    default:
                        errorPosition = "beforeend";
                        break;
                }
            }

            if (groupedFields.length > 1) {
                const parent = findCommonParent(groupedFields);
                handleMarkup(parent, response, errorPosition, true);
            } else {
                const element = form.querySelector(`[name="${typeof field == "string" ? field : field.name}"]:not([aria-hidden="true"], [type="hidden"]), [name="${typeof field == "string" ? field : field.name}[]"]:not([aria-hidden="true"], [type="hidden"])`);

                if (element != null) {
                    let parent = element.parentElement.nodeName != "LABEL" ? element.parentElement : element.parentElement.parentElement;
                    handleMarkup(parent, response, "beforeend");
                }
            }
        } else {
            let errorPosition = "afterend";

            if (props.errorPosition) {
                switch (props.errorPosition) {
                    case "beforeInput":
                        errorPosition = "beforebegin";
                        break;
                    default:
                        errorPosition = "afterend";
                        break;
                }
            }

            handleMarkup(field, response, errorPosition);
        }
    };

    const handleFormErrorsSummary = () => {
        const errorSummary = validator._value.querySelector(".validator__errors");

        errorSummary.querySelectorAll(".ff-errors").forEach(el => {
            el.remove();
        });

        if (typeof fieldErrors == "object" && fieldErrors.length) {
            fieldErrors.forEach(item => {
                let field       = item[0];
                let response    = item[1];

                handleFormErrorsSummaryMarkup(field, response);
            });
        } else {
            handleFormErrorsVisibility();
        }
    };

    const handleFormErrorsSummaryMarkup = (field, response) => {
        const errorSummary = validator._value.querySelector(".validator__errors");

        if (errorSummary != null) {
            const markupEl = buildErrorsMarkup(field, response, field.name, true);

            const existing = errorSummary.querySelector(`#${field.name}-errors`);
            if (existing != null) {
                existing.remove();
            }
            errorSummary.appendChild(markupEl);
        }

        handleFormErrorsVisibility();
    };

    const handleFormErrorsVisibility = () => {
        const errorSummary = validator._value.querySelector(".validator__errors");

        if (errorSummary.children.length) {
            errorSummary.style.display = "block";
        } else {
            errorSummary.style.display = "none";
        }
    };

    const handleFieldError = (field, response) => {
        if (typeof fieldErrors != "object") {
            fieldErrors = [[field, response]];
        } else {
            fieldErrors.push([field, response]);
        }
    };

    const handleParentClasses = (field, response) => {
        const form = validator._value.querySelector("form");

        if (typeof field == "string" || ["radio", "checkbox"].indexOf(field.type) != -1) {
            const groupedFields     = form.querySelectorAll(`[name="${typeof field == "string" ? field : field.name}"]:not([aria-hidden="true"], [type="hidden"]), [name="${typeof field == "string" ? field : field.name}[]"]:not([aria-hidden="true"], [type="hidden"])`);

            let parent              = null;

            if (groupedFields.length > 1) {
                parent = findCommonParent(groupedFields);
            } else {
                parent = groupedFields[0].parentElement;

                if (parent.tagName == "LABEL") {
                    parent = parent.parentElement;
                }
            }

            if (parent != null) {
                if (typeof response == "object" && response.errors != null) {
                    !parent.classList.contains("freeform-error") ? parent.classList.add("freeform-error") : false;
                } else {
                    parent.classList.contains("freeform-error") ? parent.classList.remove("freeform-error") : false;
                }
            }
        } else {
            const parent = field.parentElement.classList.contains("freeform-column") ? field.parentElement : null;

            if (parent != null) {
                if (typeof response == "object" && response.errors != null) {
                    !parent.classList.contains("freeform-error") ? parent.classList.add("freeform-error") : false;
                } else {
                    parent.classList.contains("freeform-error") ? parent.classList.remove("freeform-error") : false;
                }
            }
        }
    };

    const handleValidation = (field, response) => {
        const form = validator._value.querySelector("form");

        if (field.type == "string") {
            const groupedFields   = form.querySelectorAll(`[name="${typeof field == "string" ? field : field.name}"]:not([aria-hidden="true"], [type="hidden"]), [name="${typeof field == "string" ? field : field.name}[]"]:not([aria-hidden="true"], [type="hidden"])`);

            if (groupedFields.length == 1) {
                field = groupedFields[0];
            }
        }
        handleParentClasses(field, response);
        handleErrorsMarkup(field, response);
        handleAttributes(field, response);
    };

    const resetContainers = () => {
        fields = [];
        fieldSchemas = [];
    };

    const resetForm = () => {
        resetContainers();
        setupChangeEvents(true);
    };

    const scrollToFirstError = () => {
        const form = validator._value.querySelector("form");
        const firstError = form.querySelector(".freeform-error");

        if (firstError != null) {
            firstError.scrollIntoView();
        }
    };

    const setupChangeEvents = (removeEvents = false) => {
        const form = validator._value.querySelector("form");

        if (fieldSchemas.length) {
            fieldSchemas.forEach(item => {
                const field   = item[0];
                const schema  = item[1];

                function handleEventListeners(field) {
                    if (removeEvents) {
                        field.removeEventListener("change", validateFieldEvent);
                        field.removeEventListener("focusout", validateFieldEvent);
                        field.removeEventListener("input", validateFieldEvent);
                    } else {
                        if (["checkbox", "radio"].indexOf(field.type) == -1) {
                            field.addEventListener("change", validateFieldEvent);
                            field.addEventListener("focusout", validateFieldEvent);
                            field.addEventListener("input", validateFieldEvent);

                        }
                        field.addEventListener("change", validateFieldEvent);
                    }
                }

                // Wrapper function so the event listener can be removed
                function validateFieldEvent(event) {
                    let field = event.currentTarget;

                    let data = null;
                    let schema = field.schema;

                    // Check if we need to map all values from e.g. checkboxes, radio buttons
                    const groupedFields = Object.entries(form.querySelectorAll(`[name="${typeof field == "string" ? field : field.name}"]:not([aria-hidden="true"], [type="hidden"]), [name="${typeof field == "string" ? field : field.name}[]"]:not([aria-hidden="true"], [type="hidden"])`)).map(element => element[1].type == field.type ? element[1] : null).filter(element => element != null);

                    if (groupedFields != null && groupedFields.length > 1) {
                        data = groupedFields.filter(element => element.type == field.type && element.checked).map(element => element.value);
                    } else {
                        data = event.currentTarget.value;
                        if (["checkbox"].indexOf(event.currentTarget.type) != -1 && !field.checked) {
                            data = null;
                        }
                    }

                    validateField(field, schema, data);
                }

                // String identifier to get grouped fields e.g. checkboxes, radio buttons
                if (typeof field == "string") {
                    const groupedFields = fields.filter(groupedField => groupedField.attributes.name.nodeValue == field);

                    if (groupedFields.length > 1) {
                        groupedFields.forEach(groupedField => {
                            groupedField.schema = schema;

                            handleEventListeners(groupedField);
                        });
                    } else {
                        let element = groupedFields[0];
                        // Have to bind the properties like this so we can remove the event listener
                        element.schema = schema;
                        handleEventListeners(element);
                    }
                } else {
                    switch (field.type) {
                        default:
                            // Have to bind the properties like this so we can remove the event listener
                            field.schema = schema;
                            handleEventListeners(field);
                            break;
                    }
                }
            });
        }
    };

    const setupEmailSuggestion = (field) => {
        const validEmailDomain = (value) => {
            const userDomain    = value.match(domainRegex)?.[0] ?? '';
            let result;

            for (let i = 0; i < allDomains.length; i++) {
                const levenResult = leven(userDomain, allDomains[i]);
                if (typeof result === 'undefined' || levenResult < result) {
                    result = levenResult;
                }
                if (result === 0) break; // exact match, no need to continue
            }

            return result;
        };

        field.addEventListener('change', event => {
            const value     = event.target.value;
            const parent    = event.target.parentElement;

            const suggestions = parent.querySelector('.ff-suggestions');

            if (suggestions) {
                suggestions.remove();
            }

            let result = validEmailDomain(value);

            if (result > 0) {
                if (parent) {
                    const markup = buildSuggestionMarkup(field, field.name, result);

                    parent.appendChild(markup);
                }
            }
        });
    };

    const setupFFEvents = () => {
        const form = validator._value.querySelector("form");

        form.addEventListener("freeform-ajax-after-submit", function(event) {
            scrollToFirstError();
        });

        form.addEventListener("freeform-on-submit", function(event) {
            const isBackButtonPressed = event.isBackButtonPressed;

            if (!isBackButtonPressed) {
                const isValid = validateAllFields();

                if (props.showFormErrorSummary) {
                    handleFormErrorsSummary();
                }

                if (!isValid) {
                    event.preventDefault();
                    scrollToFirstError();
                }
            }
        });

        form.addEventListener("freeform-ready", function (event) {
            const options   = {
                ajax: true,
                autoScroll: false,
                disableSubmit: true,
                errorClassField: 'freeform-required',
                showProcessingSpinner: false,
                showProcessingText: true
            };

            event.options = options;
        });

        document.addEventListener("freeform-render-form-errors", function(event) {
            event.preventDefault();

            if (event.target == form) {
                validateAllFields();
            }
        });

        document.addEventListener("freeform-render-field-errors", function(event) {
            event.preventDefault();
        });
    };

    // Getting the fields from the DOM
    const setupFields = () => {
        const form = validator._value.querySelector("form");

        let fieldsArray = Object.entries(form.querySelectorAll("input, select, textarea")).filter(item => item[1].type != "hidden" && !item[1].getAttribute("aria-hidden") == true);

        fieldsArray.forEach(item => {
            const field = item[1];

            if (field.type == 'email') {
                setupEmailSuggestion(field);
            }

            if (typeof fields != "object") {
                fields = [];
                fields.push(field);
            } else {
                fields.push(field);
            }
        });
    };

    // Set up the Yup schema for each field in the form
    const setupFieldsSchema = () => {
        const form = validator._value.querySelector("form");

        const groupedTypes = ["checkbox", "radio"];

        const singleFields = fields.filter(field => !groupedTypes.includes(field.type));

        // Validate single fields
        singleFields.forEach(field => {
            const schema = buildSchema(field);

            if (schema != null) {
                if (typeof fieldSchemas != "object") {
                    fieldSchemas = [];
                    fieldSchemas.push([field, schema]);
                } else {
                    fieldSchemas.push([field, schema]);
                }
            }
        });

        // Grouped fields e.g. checkboxes, radio buttons
        groupedTypes.forEach(type => {
            const typeFields = fields.filter(field => field.type == type);

            let fieldGroups = {};

            typeFields.forEach(field => {
                if (field["name"] != null) {
                    // Check if we're working with a solo checkbox or group
                    if (form.querySelectorAll(`[type="${ type }"][name="${typeof field == "string" ? field : field.name}"]:not([aria-hidden="true"], [type="hidden"]), [type="${ type }"][name="${typeof field == "string" ? field : field.name}[]"]:not([aria-hidden="true"], [type="hidden"])`).length > 1) {
                        const groupedFields = groupFields(typeFields, "name");

                        if (fieldGroups[(field["name"])] == null) {
                            fieldGroups[(field["name"])] = groupedFields;
                        }
                    } else {
                        const schema = buildSchema(field);

                        if (schema != null) {
                            if (typeof fieldSchemas != "object") {
                                fieldSchemas = [];
                                fieldSchemas.push([field, schema]);
                            } else {
                                fieldSchemas.push([field, schema]);
                            }
                        }
                    }
                }
            });

            for (const [key, value] of Object.entries(fieldGroups)) {
                if (value[key]) {
                    const schema = buildSchema(value[key], true);
                    fieldSchemas.push([key, schema]);
                }
            };
        });
    };

    // Watch the form for layout changes e.g. when Freeform AJAX updates the DOM
    const setupMutationObserver = () => {
        const target  = validator._value;

        const config  = { attributes: false, childList: true, subtree: true };

        const callback = (mutationList, observer) => {
            for (const mutation of mutationList) {
                // Only watch for mutations at the top level within the form
                if (mutation.target.parentElement == target) {
                    if (mutation.type === "childList") {
                        // Set up the form again for validation if the DOM changes to
                        // get the new fields that have been added.
                        resetForm();
                        setupForm();
                    }
                }
            }
        };

        const observer  = new MutationObserver(callback);

        observer.observe(target, config);
    };

    // Using the Yup schema object to validate the data
    const validateField = (field, schema, data) => {
        const form = validator._value.querySelector("form");

        // String identifier to get grouped fields e.g. checkboxes, radio buttons
        if (typeof field == "string" || ["checkbox", "radio"].indexOf(field.type) != -1) {

            const groupedFields = Object.entries(form.querySelectorAll(`[name="${typeof field == "string" ? field : field.name}"]:not([aria-hidden="true"], [type="hidden"]), [name="${typeof field == "string" ? field : field.name}[]"]:not([aria-hidden="true"], [type="hidden"])`));

            if (groupedFields.length > 1) {
                // Check by first field type (they should all be the same)
                switch (groupedFields[0][1].type) {
                    case "radio":
                        // Radio should only have one value
                        data = [groupedFields.map(element => element[1].checked ? element[1].value : '').filter(item => item.length > 0)[0]];
                        break;
                    case "checkbox":
                        data = groupedFields.map(element => element[1].checked ? element[1].value : '').filter(item => item.length > 0);
                        break;
                }

                schema.validate(data)
                .then(valid => handleValidation(field, valid))
                .catch(error => { handleValidation(field, error); });
            } else {
                const element = form.querySelector(`[name="${typeof field == "string" ? field : field.name}"]:not([aria-hidden="true"], [type="hidden"]), [name="${typeof field == "string" ? field : field.name}[]"]:not([aria-hidden="true"], [type="hidden"])`);
                data = element.value;

                if (["checkbox"].indexOf(element.type) != -1 && !element.checked) {
                    data = null;
                }

                schema.validate(data)
                .then(valid => handleValidation(element, valid))
                .catch(error => { handleValidation(element, error) });
            }
        } else {
            schema.validate(data)
            .then(valid => handleValidation(field, valid))
            .catch(error => { handleValidation(field, error) });
        }
    };

    const validateAllFields = () => {
        let isValid = true;
        const form = validator._value.querySelector("form");

        if (fieldSchemas.length) {
            fieldSchemas.forEach(item => {
                const field = item[0];
                const schema = item[1];
                let data;

                // Grouped fields check
                if (typeof field == "string") {
                    const groupedFields = Object.entries(form.querySelectorAll(`[name="${typeof field == "string" ? field : field.name}"]:not([aria-hidden="true"], [type="hidden"]), [name="${typeof field == "string" ? field : field.name}[]"]:not([aria-hidden="true"], [type="hidden"])`));

                    data = groupedFields.filter(element => element[1].checked).map(element => element[1].value);
                } else {
                    data = ["checkbox", "radio"].indexOf(field.type) != -1 ? (field.checked ? field.value : null) : field.value;
                }

                try {
                    schema.validateSync(data);

                    fieldErrors = typeof fieldErrors == "object" ? fieldErrors.filter(item => item[0].name != field.name) : fieldErrors;
                } catch (error) {
                    handleFieldError(field, error);
                    handleValidation(field, error);
                    isValid = false;
                }
            });
        }

        return isValid;
    };

    onMounted(() => {
        allDomains = getDomains();
        setupFFEvents();
        setupForm();
        setupMutationObserver();
    });

    onUpdated(() => {
        setupForm();
        setupMutationObserver();
    });
</script>
